import { RefObject, useEffect, useRef, useState } from 'react';

import {
  SUPERSET_DASHBOARD_UI_CONFIG,
  SUPERSET_DOMAIN,
  USER_FACING_ERROR_MESSAGE,
} from './constants';
import { loadSupersetSdk } from './loadSupersetSdk';
import {
  fetchSupersetDashboardId,
  fetchSupersetGuestToken,
  SupersetForbiddenError,
} from './supersetApi';
import { createHeightManager } from './supersetHeightManager';
import { SupersetEmbeddedDashboard } from './types';

type UseSupersetAnalyticsEmbedResult = {
  mountRef: RefObject<HTMLDivElement>;
  forbidden: boolean;
  isCheckingAccess: boolean;
  isLoading: boolean;
  errorMessage: string | null;
};

export function useSupersetAnalyticsEmbed(): UseSupersetAnalyticsEmbedResult {
  const mountRef = useRef<HTMLDivElement>(null);
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const embeddedRef = useRef<SupersetEmbeddedDashboard | null>(null);
  const [dashboardId, setDashboardId] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const checkAccess = async () => {
      setIsCheckingAccess(true);
      setDashboardId(null);
      setForbidden(false);
      setErrorMessage(null);

      try {
        const nextDashboardId = await fetchSupersetDashboardId();
        if (!isMounted) return;

        setDashboardId(nextDashboardId);
      } catch (err) {
        if (!isMounted) return;

        if (err instanceof SupersetForbiddenError) {
          setForbidden(true);
          return;
        }

        setErrorMessage(USER_FACING_ERROR_MESSAGE);

        if (process.env.NODE_ENV === 'development' && err instanceof Error) {
          console.error('[SupersetAnalyticsBlock]', err.message, err);
        }
      } finally {
        if (isMounted) {
          setIsCheckingAccess(false);
        }
      }
    };

    checkAccess();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (isCheckingAccess || !dashboardId || forbidden) {
      return undefined;
    }

    let isMounted = true;
    let heightManager: ReturnType<typeof createHeightManager> | null = null;

    const runEmbed = async () => {
      const mountPoint = mountRef.current;
      if (!mountPoint) {
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const { sdk, script } = await loadSupersetSdk();
        if (script) {
          scriptRef.current = script;
        }
        if (!sdk || !isMounted) {
          return;
        }

        const embedded = (await sdk.embedDashboard({
          id: dashboardId,
          supersetDomain: SUPERSET_DOMAIN,
          mountPoint,
          fetchGuestToken: fetchSupersetGuestToken,
          dashboardUiConfig: SUPERSET_DASHBOARD_UI_CONFIG,
        })) as SupersetEmbeddedDashboard;

        if (!isMounted) {
          return;
        }

        embeddedRef.current = embedded;

        // getScrollSize() resolves once the Superset React app initialises
        // (messages are queued in MessagePort until the app calls port.start()).
        const initialSize = await embedded.getScrollSize();

        if (!isMounted) {
          return;
        }

        if (initialSize?.height > 0) {
          mountPoint.style.height = `${initialSize.height}px`;
        }

        // Spinner off — user sees the app shell.
        // Charts may still be loading; height manager polls until stable.
        setIsLoading(false);

        heightManager = createHeightManager(() => embedded.getScrollSize(), mountPoint);
        heightManager.startStabilization();
        heightManager.attachResizeListeners();
      } catch (err) {
        if (!isMounted) {
          return;
        }

        if (err instanceof SupersetForbiddenError) {
          setDashboardId(null);
          setForbidden(true);
          setErrorMessage(null);
          setIsLoading(false);
          return;
        }

        setErrorMessage(USER_FACING_ERROR_MESSAGE);
        setIsLoading(false);
        if (process.env.NODE_ENV === 'development' && err instanceof Error) {
          console.error('[SupersetAnalyticsBlock]', err.message, err);
        }
      }
    };

    runEmbed();

    return () => {
      isMounted = false;
      heightManager?.dispose();
      embeddedRef.current?.unmount();
      embeddedRef.current = null;
      if (scriptRef.current?.parentNode) {
        scriptRef.current.remove();
        scriptRef.current = null;
      }
    };
  }, [dashboardId, forbidden, isCheckingAccess]);

  return { mountRef, forbidden, isCheckingAccess, isLoading, errorMessage };
}
